import { Box, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
import type { InfringementStatus } from 'src/context/api/brandInfringement/api';
import { STATUS_CONFIG } from './statusConfig';

const GAUGE_MAX_SEGMENTS = 7;

// Low presence is good news, so the scale runs green → yellow → red as the rate climbs.
export function presenceColor(value: number): string {
    if (value > 66) return '#dc1818';
    if (value > 33) return '#efcf61';
    return '#118d57';
}

/**
 * The integer the cell prints. Shared with the column's `valueGetter` so filtering on the rate
 * compares against the number the user actually reads, not the raw float behind it.
 */
export const presenceDisplayValue = (value: number) => Math.round(Math.min(100, Math.max(0, value)));

export function PaidPresenceRateCell({ value }: { value: number }) {
    const clamped = Math.min(100, Math.max(0, value));
    return (
        <Stack direction="row" alignItems="center" spacing={1} width="100%">
            <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {presenceDisplayValue(value)}%
            </Typography>
            <Box sx={{ flex: 1, height: 6, borderRadius: 6, bgcolor: 'rgba(145,158,171,0.2)', overflow: 'hidden' }}>
                <Box sx={{ width: `${clamped}%`, height: '100%', borderRadius: 24, bgcolor: presenceColor(clamped) }} />
            </Box>
        </Stack>
    );
}

export function ActiveDaysGaugeCell({ activeDays, totalDays }: { activeDays: number; totalDays: number }) {
    const segments = Math.min(totalDays || GAUGE_MAX_SEGMENTS, GAUGE_MAX_SEGMENTS);
    const filled = totalDays <= GAUGE_MAX_SEGMENTS
        ? activeDays
        : Math.round((activeDays / (totalDays || 1)) * GAUGE_MAX_SEGMENTS);
    return (
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end" width="100%">
            <Typography variant="body2" fontWeight={500}>{activeDays}</Typography>
            <Stack direction="row" spacing="4px">
                {Array.from({ length: segments }).map((_, i) => (
                    <Box
                        key={i}
                        data-testid="gauge-segment"
                        data-active={i < filled ? 'true' : 'false'}
                        sx={{ width: 4, height: 14, borderRadius: '2px', bgcolor: i < filled ? '#1e88e5' : '#dfe3e8' }}
                    />
                ))}
            </Stack>
        </Stack>
    );
}

// These pills are not clickable, but the theme's MuiChip override repaints every filled/default
// chip with `grey[700]` on hover from the component root (overrides/components/chip.tsx) — MUI's
// own hover is scoped to `.MuiChip-clickable`, that one isn't. Pinned back to the base tint so the
// pill stays inert, like the search-term chips below.
const placeChipSx = {
    bgcolor: 'rgba(183,220,251,0.2)', borderRadius: '28px', height: 26,
    fontSize: 14, fontWeight: 500, color: '#333c47',
    '&:hover': { bgcolor: 'rgba(183,220,251,0.2)' },
};

type PlaceCounts = { titleOnly: number; descOnly: number; both: number };

/** Every place a cell can show, in cell order — the filter's options. */
export const PLACE_OPTIONS = ['Title', 'Description', 'Title + Description'] as const;

/**
 * The places a row infringes on. The chips abbreviate the last one when they are crowded, so this
 * returns the unabbreviated labels: it is the row's filterable value, and `PLACE_OPTIONS` is the
 * list the filter offers.
 */
export function placeLabels({ titleOnly, descOnly, both }: PlaceCounts): string[] {
    return [
        titleOnly > 0 && 'Title',
        descOnly > 0 && 'Description',
        both > 0 && 'Title + Description',
    ].filter((label): label is string => typeof label === 'string');
}

export function PlaceChipsCell({ titleOnly, descOnly, both }: PlaceCounts) {
    const labels = placeLabels({ titleOnly, descOnly, both });
    const chips = labels.map((label) =>
        label === 'Title + Description' && labels.length > 1 ? 'Title + Descrip.' : label
    );
    return (
        <Stack direction="row" spacing={1}>
            {chips.map((label) => <Chip key={label} label={label} size="small" sx={placeChipSx} />)}
        </Stack>
    );
}

// A clickable MUI Chip repaints itself on hover with `alpha(palette.action.selected, …)` — a generic
// grey unrelated to the chip's own colour — and raises itself with a shadow on `:active`. Both are
// pinned back to the base tint here so the chip never reacts visually to hover, focus or press.
const searchTermChipSx = {
    bgcolor: 'rgba(145,158,171,0.1)',
    borderRadius: '32px',
    fontSize: 14,
    color: '#333c47',
    flexShrink: 0,
    '&:hover': { bgcolor: 'rgba(145,158,171,0.1)' },
    '&.Mui-focusVisible': { bgcolor: 'rgba(145,158,171,0.1)' },
    '&:active': { bgcolor: 'rgba(145,158,171,0.1)', boxShadow: 'none' },
};

export function SearchTermsCell({ terms, maxVisible = 3, onTermClick }: {
    terms: string[]; maxVisible?: number; onTermClick?: (term: string) => void;
}) {
    const visible = terms.slice(0, maxVisible);
    const hidden = terms.length - visible.length;
    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%', overflow: 'hidden' }}>
                {visible.map((term) => (
                    <Chip
                        key={term}
                        label={term}
                        size="small"
                        onClick={onTermClick ? () => onTermClick(term) : undefined}
                        sx={searchTermChipSx}
                    />
                ))}
            </Stack>
            {hidden > 0 && (
                <Box
                    sx={{
                        position: 'absolute', right: 0, top: 0, height: '100%',
                        display: 'flex', alignItems: 'center', px: 1,
                        bgcolor: 'rgba(240,240,240,0.7)', backdropFilter: 'blur(1px)',
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#2f3843', fontVariantNumeric: 'tabular-nums' }}>
                        +{hidden}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export function StatusActionCell({ status, onStartCapture, onDownload, busy = false }: {
    status: InfringementStatus;
    onStartCapture: () => void;
    onDownload: () => void;
    busy?: boolean;
}) {
    const cfg = STATUS_CONFIG[status];
    const pill = (
        <Stack
            direction="row" alignItems="center" justifyContent="center" spacing={0.5}
            sx={{
                width: 132, height: 24, px: 1, borderRadius: '32px', bgcolor: cfg.bg,
                color: cfg.color, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden',
                flexShrink: 0, lineHeight: 1,
            }}
        >
            <Iconify icon={cfg.icon} width={12} />
            <span>{cfg.label}</span>
        </Stack>
    );
    const isDisabled = cfg.action === 'play-disabled';
    return (
        <Stack direction="row" alignItems="center" spacing="10px">
            {cfg.tooltip ? <Tooltip title={cfg.tooltip}>{pill}</Tooltip> : pill}
            {cfg.action === 'loading' || busy ? (
                <Box
                    sx={{
                        width: 26, height: 26, borderRadius: '6px', bgcolor: 'rgba(226,229,233,0.22)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <CircularProgress size={14} />
                </Box>
            ) : (
                <IconButton
                    size="small"
                    disabled={isDisabled}
                    onClick={cfg.action === 'download' ? onDownload : onStartCapture}
                    sx={{ width: 26, height: 26, borderRadius: '6px', bgcolor: 'rgba(226,229,233,0.62)', opacity: isDisabled ? 0.2 : 1 }}
                >
                    <Iconify icon={cfg.action === 'download' ? 'eva:download-outline' : 'eva:play-circle-outline'} width={14} />
                </IconButton>
            )}
        </Stack>
    );
}
